import useGetBannerAdExchange from '@/hooks/ad/useGetBannerAdExchange';
import useHandleAdExposure from '@/hooks/ad/useHandleAdExposure';
import useLogger from '@/hooks/useLogger';
import { AdPageType, getServiceAdType } from '@/models/ad/list';
import { Client } from '@/types/app';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';

const convertAdViewerOsPlatformByClient = (client: Client) => {
  if (!client.isWebView) {
    return 'web';
  }
  if (client.isAndroid) {
    return 'android';
  }
  return 'ios';
};

const useAdBanner = (adPageType: AdPageType, client: Client, enabled = true) => {
  const router = useRouter();
  const logger = useLogger();
  const ref = useRef<string>();
  const targetType = getServiceAdType(adPageType, client);

  const { bannerAdExchange, refetchBannerAdExchange, dataUpdatedAt } = useGetBannerAdExchange(
    targetType,
    client,
    enabled
  );
  const { sendExposureLog } = useHandleAdExposure();

  const handleSendExposureLog = useCallback(() => {
    const adViewerOsPlatform = convertAdViewerOsPlatformByClient(client);
    const exposedAt = new Date().valueOf();

    if (bannerAdExchange && !!dataUpdatedAt) {
      logger.log({
        eventName: 'ad_banner_impression',
        payload: {
          targetType: bannerAdExchange.targetPlace,
          bannerAdId: bannerAdExchange.bannerAdId,
          adViewerOsPlatform: adViewerOsPlatform,
          exposedAt: exposedAt,
        },
      });
      sendExposureLog(bannerAdExchange.bannerAdId, exposedAt, adViewerOsPlatform);
    }
  }, [bannerAdExchange, dataUpdatedAt, client]);

  useEffect(() => {
    if (ref.current !== undefined && ref.current !== router.pathname) {
      refetchBannerAdExchange();
    }
    ref.current = router.pathname;
  }, [router.pathname]);

  return {
    handleSendExposureLog,
    bannerAdExchange,
  };
};

export default useAdBanner;
